'use client';
import { useState } from 'react';

interface Props {
  search: string;
  onSearch: (s: string) => void;
  allStocks?: Array<{ symbol: string; name: string }>;
}

export function SearchBar({ search, onSearch, allStocks }: Props) {
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  return (
    <div className="px-5 py-1.5 border-b border-[#1e293b]">
      <div className="relative">
        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchInput || search}
          onChange={e => {
            const v = e.target.value;
            setSearchInput(v);
            onSearch(v.toUpperCase());
          }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
          placeholder="Search symbol or company..."
          className="w-full bg-[#111827] text-white border border-[#1e293b] rounded-lg pl-8 pr-8 py-1.5 text-[12px]
                     placeholder-[#334155] focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20
                     transition-all duration-150"
          style={{ fontFamily: 'var(--font-mono)' }}
        />
        {(searchInput || search) && (
          <button
            onClick={() => { setSearchInput(''); onSearch(''); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 text-[#64748b] hover:text-white transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {searchFocused && searchInput.length >= 1 && allStocks && (() => {
          const q = searchInput.toLowerCase();
          const matches = allStocks
            .filter(s => {
              if (s.symbol.toLowerCase().startsWith(q)) return true;
              if (q.length >= 2 && s.name.toLowerCase().includes(q)) return true;
              return false;
            })
            .sort((a, b) => {
              const aExact = a.symbol.toLowerCase() === q ? 0 : 1;
              const bExact = b.symbol.toLowerCase() === q ? 0 : 1;
              if (aExact !== bExact) return aExact - bExact;
              const aSymStart = a.symbol.toLowerCase().startsWith(q) ? 0 : 1;
              const bSymStart = b.symbol.toLowerCase().startsWith(q) ? 0 : 1;
              if (aSymStart !== bSymStart) return aSymStart - bSymStart;
              return a.symbol.localeCompare(b.symbol);
            })
            .slice(0, 8);
          if (matches.length === 0) return null;
          return (
            <div className="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden z-50"
              style={{ background: 'rgba(10, 12, 20, 0.98)', border: '1px solid #334155' }}>
              {matches.map(s => (
                <button
                  key={s.symbol}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 cursor-pointer"
                  onMouseDown={() => {
                    setSearchInput(s.symbol);
                    onSearch(s.symbol);
                    setSearchFocused(false);
                  }}
                >
                  <span className="text-white text-[11px] font-semibold" style={{ minWidth: 52, fontFamily: 'var(--font-mono)' }}>{s.symbol}</span>
                  <span className="text-[#64748b] text-[10px] truncate">{s.name}</span>
                </button>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
